import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import { ListedUser as User, Friend, PendingFriendRequest } from '../types';

export default function Users() {
  const { user: currentUser } = useAuth();
  const { addNotification } = useNotification();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ pages: 1 });
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PendingFriendRequest[]>([]);
  const [friendStatusLoading, setFriendStatusLoading] = useState(true);
  const [sendingFriendRequestId, setSendingFriendRequestId] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
    if (currentUser && !currentUser.isGuest) {
      fetchFriendStatuses();
    }
  }, [page, currentUser]);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/users?page=${page}&limit=10`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json() as { error: string };
        throw new Error(errorData.error || 'Failed to fetch users');
      }

      const data = await response.json() as { 
        users: { results: User[] }, 
        pagination: { page: number; limit: number; total: number; pages: number }
      };
      
      if (data.users && Array.isArray(data.users.results)) {
        setUsers(data.users.results);
      } else {
        console.error('Unexpected API response structure for users:', data);
        setUsers([]);
      }
      
      if (data.pagination) {
        setPagination(data.pagination);
      } else {
         console.error('Unexpected API response structure for pagination:', data);
         setPagination({ pages: 1 });
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setUsers([]);
      setPagination({ pages: 1 });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch friends and pending requests to determine button states
  const fetchFriendStatuses = async () => {
    setFriendStatusLoading(true);
    try {
      // Fetch accepted friends
      const friendsRes = await fetch('/api/friends', { credentials: 'include' });
      const friendsData = await friendsRes.json();
      if (!friendsRes.ok) throw new Error((friendsData as {error: string}).error || 'Failed to fetch friends');
      setFriends(Array.isArray(friendsData) ? friendsData.map((f: any) => ({ friend_id: f.friend_id, friend_username: f.friend_username })) : []);

      // Fetch pending requests 
      const pendingRes = await fetch('/api/friends/requests/pending', { credentials: 'include' });
      const pendingData: any = await pendingRes.json();
      if (!pendingRes.ok) throw new Error((pendingData as {error: string}).error || 'Failed to fetch pending requests');
      
      // Now check array and map
      const mappedRequests: PendingFriendRequest[] = Array.isArray(pendingData) 
        ? pendingData.map((p: any) => ({ 
            sender_id: p.sender_id, 
            sender_username: p.sender_username, 
            requested_at: p.requested_at 
          })) 
        : [];
      setPendingRequests(mappedRequests);
      // console.warn("Pending requests endpoint needs adjustment to provide user IDs for status checking.");
      // setPendingRequests([]); // Keep it empty for now
    } catch (err) {
      console.error("Error fetching friend statuses:", err);
      // Don't block the user list if this fails
      setError(prev => prev ? `${prev}\nFailed to load friend statuses.` : 'Failed to load friend statuses.');
    } finally {
      setFriendStatusLoading(false);
    }
  };

  const handleSendFriendRequest = async (userId: string, recipientUsername: string) => {
    setError('');
    setSendingFriendRequestId(userId);
    try {
      const response = await fetch('/api/friends/requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ recipientUsername })
      });
      const data = await response.json() as { error?: string; message?: string };
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send friend request');
      }
      addNotification(`Friend request sent to ${recipientUsername}!`, 'success');
    } catch (err) { 
      const errorMsg = err instanceof Error ? err.message : 'Failed to send friend request';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setSendingFriendRequestId(null);
    }
  };

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Define Skeleton Row
  const SkeletonUserRow = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div></td>
      <td className="px-6 py-4"><div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded"></div></td>
    </tr>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary dark:border-primary-light"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Users</h1>
        <div className="relative">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
          />
          <svg
            className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 dark:text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="shadow-md dark:shadow-none rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Collections
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Games Played
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                // Render Skeleton Rows if loading
                [...Array(10)].map((_, i) => <SkeletonUserRow key={i} />) 
              ) : filteredUsers.length > 0 ? (
                // Render User Rows if not loading and users exist
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {user.collection_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {user.games_played}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(user.created_at).toLocaleString()}
                    </td>
                    {/* Actions Column */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {(() => {
                        if (!currentUser || currentUser.isGuest || user.is_guest || user.id === currentUser.id || friendStatusLoading) {
                          return null; 
                        }
                        const isFriend = friends.some(f => f.friend_id === user.id); 
                        const isPending = pendingRequests.some(p => p.sender_id === user.id);
                        const isSending = sendingFriendRequestId === user.id;

                        if (isFriend) {
                          return <span className="text-xs text-gray-500 dark:text-gray-400 italic">Already friends</span>;
                        } else if (isPending) {
                          return <span className="text-xs text-gray-500 dark:text-gray-400 italic">Request pending</span>;
                        } else {
                          return (
                            <button
                              onClick={() => handleSendFriendRequest(user.id, user.username)}
                              className="btn-secondary text-xs p-1"
                              title={`Send friend request to ${user.username}`}
                              disabled={isSending}
                            >
                              {isSending ? (
                                <svg className="animate-spin h-4 w-4 text-primary" /*...*/></svg>
                              ) : (
                                <UserPlusIcon className="h-4 w-4" />
                              )}
                            </button>
                          );
                        }
                      })()}
                    </td>
                  </tr>
                ))
              ) : (
                // Render No Users Found Row if not loading and no users
                <tr>
                  <td colSpan={6} className="text-center py-12 px-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">No users found</h3>
                    {searchTerm && (
                      <p className="mt-2 text-gray-500 dark:text-gray-400">
                        Try adjusting your search term
                      </p>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex justify-center items-center space-x-2 mt-6">
        <button
          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
          disabled={page === 1}
          className="btn-secondary text-sm disabled:opacity-50"
        >
          Previous
        </button>
        <button
          onClick={() => setPage(prev => Math.min(prev + 1, pagination.pages))}
          disabled={page === pagination.pages}
          className="btn-secondary text-sm disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
} 